import { ComponentProps, FC, useEffect, useState } from 'react';
import { ActionMeta, CSSObjectWithLabel } from 'react-select';
import makeAnimated from 'react-select/animated';
import CreatableSelect from 'react-select/creatable';
import { SearchByInputType } from 'src/graphql';
import {
  DefaultSearchOptionsType,
  SearchValueProps,
} from 'src/hooks/table/useTableColumns';
import { atlasTypographyLengthToPx } from 'src/theme/atlas';
import { useTheme } from 'styled-components';
import {
  PaginatedTableSearchContainer,
  StyledCreatableSelect,
} from './PaginatedTableSearch.styles';

interface Props {
  options: { label: string; value: string; color?: string }[];
  applyFilter?: (value: SearchValueProps, meta?: unknown) => void;
  paramSearchValues?: DefaultSearchOptionsType[] | null;
  setCaseQueryParamFields?: (value: SearchByInputType[] | null) => void;
  resetParams?: () => void;
  updateUrlParams?: (value: SearchValueProps) => void;
}

export const PaginatedTableSearch: FC<Props> = ({
  options,
  applyFilter,
  paramSearchValues,
  setCaseQueryParamFields,
  resetParams,
  updateUrlParams,
}) => {
  const theme = useTheme();
  const dropdownTypography = theme.atlas.typography.semantic.bodySmall;
  const selectTextStyle = {
    fontFamily: dropdownTypography.fontFamily,
    fontSize: atlasTypographyLengthToPx(dropdownTypography.fontSize),
    lineHeight: atlasTypographyLengthToPx(dropdownTypography.lineHeight),
    fontWeight: dropdownTypography.fontWeight,
    letterSpacing: atlasTypographyLengthToPx(dropdownTypography.letterSpacing),
  } as const;
  const animatedComponents = makeAnimated();
  const [searchValue, setSearchValue] =
    useState<ComponentProps<typeof CreatableSelect>['value']>();

  useEffect(() => {
    if (paramSearchValues && paramSearchValues.length > 0) {
      setSearchValue(paramSearchValues);
    }
  }, [paramSearchValues]);

  const handleOnchange = (
    newValue: unknown,
    actionMeta: ActionMeta<unknown>,
  ) => {
    const selectedOption = newValue as SearchValueProps;
    applyFilter?.(selectedOption, actionMeta);
    setSearchValue(selectedOption);
    updateUrlParams?.(selectedOption);

    if (
      (actionMeta.action === 'clear' || actionMeta.action === 'pop-value') &&
      setCaseQueryParamFields &&
      resetParams
    ) {
      setCaseQueryParamFields(null);
      resetParams();
    }
  };

  return (
    <PaginatedTableSearchContainer data-testid="paginated-table-search">
      <StyledCreatableSelect
        closeMenuOnSelect={true}
        onChange={handleOnchange}
        components={animatedComponents}
        defaultValue={[]}
        openMenuOnFocus={false}
        openMenuOnClick={false}
        placeholder="Search"
        value={searchValue}
        isMulti
        formatCreateLabel={(value: string) => `Search for ${value}`}
        options={options}
        styles={{
          control: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
            cursor: 'pointer',
            borderColor: theme.atlas.core.color.gray[400],
          }),
          placeholder: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
            color: theme.atlas.semantic.color.type.default,
          }),
          input: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
          }),
          singleValue: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
          }),
          multiValue: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
          }),
          multiValueLabel: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
          }),
          valueContainer: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
          }),
          option: (
            baseStyles: CSSObjectWithLabel,
            {
              isFocused,
              isSelected,
            }: { isFocused: boolean; isSelected: boolean },
          ) => ({
            ...baseStyles,
            ...selectTextStyle,
            backgroundColor:
              isFocused || isSelected
                ? theme.atlas.semantic.color.action.secondary.hoverFill
                : theme.atlas.semantic.color.surface.default,
            color: theme.atlas.semantic.color.type.default,
            cursor: 'pointer',
          }),
          menu: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
            borderColor: theme.atlas.core.color.gray[400],
          }),
          menuList: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            ...selectTextStyle,
          }),
        }}
      />
    </PaginatedTableSearchContainer>
  );
};
